/** @jsxImportSource preact */
import type { SupportRequestCard } from "@go-novus/admin-api";
import { useContext, useEffect } from "preact/hooks";

import * as m from "../../paraglide/messages.js";
import { FormButton } from "../form/form-button";
import { FORM_STYLES, formVars } from "../form/form-styles";
import { StatusCard } from "../cards/status-card";
import type { WidgetTokens } from "../theme";
import { SupportRequestContext } from "./support-request-card";

/** 没发出的请求在消息里只留这一个入口，点了打开请求浮层。 */
export function RequestEntry({
  tokens,
  onOpen,
}: {
  tokens: WidgetTokens;
  onOpen: () => void;
}) {
  return (
    <div style={formVars(tokens)}>
      <style>{FORM_STYLES}</style>
      <FormButton
        tokens={tokens}
        variant="secondary"
        onClick={() => {
          onOpen();
        }}
      >
        {m.widget_verify_contact_store()}
      </FormButton>
    </div>
  );
}

/** 消息流只保留入口或短回执；轮询跟随回执，收起浮层后仍核实原请求。 */
export function SupportRequestReceipt({
  card,
  tokens,
  onOpen,
}: {
  card: SupportRequestCard;
  tokens: WidgetTokens;
  onOpen?: () => void;
}) {
  const operate = useContext(SupportRequestContext)?.operate;
  const status =
    card.lifecycle === "active"
      ? (card.payload.send_status ?? "draft")
      : undefined;
  const requestId = card.payload.request_id;
  useEffect(() => {
    if (!operate || !["sending", "unknown", "accepted"].includes(status ?? ""))
      return;
    let running = false;
    const poll = () => {
      if (running) return;
      running = true;
      void operate({ operation: "read", request_id: requestId }).finally(() => {
        running = false;
      });
    };
    const timers = [2000, 5000, 10000].map((delay) => setTimeout(poll, delay));
    const foreground = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", foreground);
    return () => {
      timers.forEach(clearTimeout);
      document.removeEventListener("visibilitychange", foreground);
    };
  }, [operate, requestId, status]);
  if (card.lifecycle !== "active")
    return (
      <StatusCard
        tokens={tokens}
        icon="lock"
        title={
          card.lifecycle === "expired"
            ? m.widget_support_expired_title()
            : m.widget_support_unavailable_title()
        }
        eyebrow={m.widget_support_reference({ id: requestId })}
        description={
          card.lifecycle === "expired"
            ? m.widget_support_expired()
            : m.widget_support_inaccessible()
        }
      />
    );
  if (
    !["sending", "unknown", "accepted", "delivered", "bounced"].includes(
      status ?? "",
    )
  )
    // 没发出的请求只留入口；同一条消息里的订单卡、验证卡已有入口时什么都不画。
    return onOpen ? <RequestEntry tokens={tokens} onOpen={onOpen} /> : null;
  return (
    <div class="gn-support-receipt" role="status" style={formVars(tokens)}>
      <style>{`.gn-support-receipt { color:var(--gn-form-quiet); font-size:13px; line-height:1.5; overflow-wrap:anywhere; } .gn-support-receipt p { margin:0; } .gn-support-receipt strong { color:var(--gn-form-ink); font-weight:600; }`}</style>
      <p>
        <strong>
          {status === "sending" || status === "unknown"
            ? m.widget_request_sending_title()
            : m.widget_request_receipt_sent()}
        </strong>
      </p>
      {!["sending", "unknown"].includes(status ?? "") && (
        <p>
          {m.widget_email_result_next_reply({
            email: card.payload.contact_email,
          })}
        </p>
      )}
    </div>
  );
}
